import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Swatch } from '@/components/ui/swatch';

export interface ComboboxItem {
  value: string;
  label: string;
  /** Secondary text (e.g. project code or "personal"). */
  hint?: string;
  color?: string;
  disabled?: boolean;
}

interface ComboboxProps {
  id?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  items: ComboboxItem[];
  /** Values shown first under a "Recent" heading. */
  recentValues?: string[];
  placeholder: string;
  searchPlaceholder?: string;
  /** Notified as the user types, for server-side searching. */
  onSearchChange?: (query: string) => void;
  /** Renders a "Create …" action when the query matches nothing exactly. */
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => ReactNode;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/** Searchable single-select built on cmdk (listbox semantics, keyboard navigable). */
export function Combobox({
  id,
  value,
  onChange,
  items,
  recentValues = [],
  placeholder,
  searchPlaceholder,
  onSearchChange,
  onCreate,
  createLabel,
  disabled,
  className,
  ...aria
}: ComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listId = useId();
  const selected = items.find((i) => i.value === value) ?? null;
  const recent = recentValues
    .map((v) => items.find((i) => i.value === v))
    .filter((i): i is ComboboxItem => !!i);
  const rest = items.filter((i) => !recentValues.includes(i.value));
  const trimmed = query.trim();
  const exactMatch = items.some((i) => i.label.toLowerCase() === trimmed.toLowerCase());
  const showCreate = !!onCreate && trimmed.length > 0 && !exactMatch;

  const pick = (v: string) => {
    onChange(v === value ? null : v);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive',
            !selected && 'text-muted-foreground',
            className,
          )}
          {...aria}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected?.color ? (
              <Swatch color={selected.color} className="h-2.5 w-2.5 shrink-0" />
            ) : null}
            <span className="truncate">{selected ? selected.label : placeholder}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-64 p-0">
        <Command shouldFilter={true} loop>
          <Command.Input
            value={query}
            onValueChange={(v) => {
              setQuery(v);
              onSearchChange?.(v);
            }}
            placeholder={searchPlaceholder ?? t('app.search')}
            className="h-9 w-full border-b bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          <Command.List id={listId} className="max-h-64 overflow-y-auto p-1">
            <Command.Empty className="px-2 py-3 text-sm text-muted-foreground">
              {t('app.noResults')}
            </Command.Empty>
            {recent.length > 0 ? (
              <Command.Group
                heading={t('time.recent')}
                className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:text-muted-foreground"
              >
                {recent.map((item) => (
                  <ComboboxOption
                    key={`recent-${item.value}`}
                    item={item}
                    selected={item.value === value}
                    onSelect={pick}
                  />
                ))}
              </Command.Group>
            ) : null}
            <Command.Group>
              {rest.map((item) => (
                <ComboboxOption
                  key={item.value}
                  item={item}
                  selected={item.value === value}
                  onSelect={pick}
                />
              ))}
            </Command.Group>
            {showCreate ? (
              <Command.Group forceMount>
                <Command.Item
                  value={`__create__ ${trimmed}`}
                  forceMount
                  onSelect={() => {
                    onCreate?.(trimmed);
                    setOpen(false);
                    setQuery('');
                  }}
                  className="flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-primary data-[selected=true]:bg-accent"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  <span>{createLabel ? createLabel(trimmed) : trimmed}</span>
                </Command.Item>
              </Command.Group>
            ) : null}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ComboboxOption({
  item,
  selected,
  onSelect,
}: {
  item: ComboboxItem;
  selected: boolean;
  onSelect: (v: string) => void;
}) {
  return (
    <Command.Item
      value={item.label}
      keywords={item.hint ? [item.hint] : undefined}
      disabled={item.disabled}
      onSelect={() => onSelect(item.value)}
      className="flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent"
    >
      <Check
        className={cn('h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
        aria-hidden="true"
      />
      {item.color ? <Swatch color={item.color} className="h-2.5 w-2.5 shrink-0" /> : null}
      <span className="truncate">{item.label}</span>
      {item.hint ? (
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">{item.hint}</span>
      ) : null}
    </Command.Item>
  );
}
